import React from "react"
import { useTranslation } from "react-i18next"
import {
  withFormik,
  FormikProps,
  FormikErrors,
  Form,
  Field,
  FieldProps,
} from "formik"
import { Box, Typography, styled } from "@mui/material"

import i18n from "config/i18n"
import Button from "components/ui/Submit"
import InputCheckbox from "components/ui/InputCheckbox"
import { ConsentSignFormValues } from "types"
import { colors } from "theme"

interface FormProps {
  initialValues: ConsentSignFormValues
  onSubmit: (values: ConsentSignFormValues) => void
}

type Props = FormikProps<ConsentSignFormValues> & FormProps

const ConsentSignForm = ({ values, errors, onSubmit, isSubmitting }: Props) => {
  const { t } = useTranslation()

  const { plusplus, freshfinance, advertisement } = values

  const handleClick = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()

    onSubmit(values)
  }

  const renderLabel = (name: string) => {
    const label = t(`features.refinance.form.${name}.label`, {
      link: `<a href=${t(
        `features.refinance.form.${name}.link.url`,
      )} target="_blank">${t(
        `features.refinance.form.${name}.link.title`,
      )}</a>`,
    })

    return <span dangerouslySetInnerHTML={{ __html: label }} />
  }

  return (
    <Form>
      <AcceptAll disabled={isSubmitting} onClick={handleClick}>
        {t("features.refinance.form.accept")}
      </AcceptAll>
      <FormGroup>
        <Field name="plusplus">
          {({ field }: FieldProps) => (
            <InputCheckbox
              {...field}
              label={renderLabel("plusplus")}
              helperText={errors.plusplus}
              checked={plusplus}
              disabled={isSubmitting}
            />
          )}
        </Field>
      </FormGroup>
      <FormGroup>
        <Field name="freshfinance">
          {({ field }: FieldProps) => (
            <InputCheckbox
              {...field}
              label={renderLabel("freshfinance")}
              helperText={errors.freshfinance}
              checked={freshfinance}
              disabled={isSubmitting}
            />
          )}
        </Field>
      </FormGroup>
      <FormGroup>
        <Field name="advertisement">
          {({ field }: FieldProps) => (
            <InputCheckbox
              {...field}
              label={renderLabel("advertisement")}
              helperText={errors.advertisement}
              checked={advertisement}
              disabled={isSubmitting}
            />
          )}
        </Field>
      </FormGroup>
      <Save type="submit" disabled={isSubmitting}>
        {t("features.refinance.form.submit")}
      </Save>
    </Form>
  )
}

const ConsentSignFormWithFormik = withFormik<FormProps, ConsentSignFormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues }) => initialValues,
  handleSubmit: (values, { props: { onSubmit } }) => onSubmit(values),
  validate: (values: ConsentSignFormValues) => {
    const errors: FormikErrors<ConsentSignFormValues> = {}

    const { plusplus, freshfinance } = values

    if (!plusplus) {
      errors.plusplus = i18n.t("validation.field.required", {
        field: i18n.t("features.refinance.form.plusplus.title"),
      })
    }

    if (!freshfinance) {
      errors.freshfinance = i18n.t("validation.field.required", {
        field: i18n.t("features.refinance.form.freshfinance.title"),
      })
    }

    return errors
  },
})(ConsentSignForm)

export default ConsentSignFormWithFormik

const FormGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "space-between",
  margin: "7px 0",
  [theme.breakpoints.up("md")]: {},
}))

export const Description = styled(Typography)(() => ({
  color: colors.black,
  fontSize: "0.875rem",
  fontWeight: "400",
  lineHeight: "1.6",
  margin: "7px 0",
}))

const Btn = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: "21px",
  display: "block",
  fontWeight: "bold",
  margin: "16px 0",
  textAlign: "center",
  width: "236px",
}))

const AcceptAll = styled(Btn)(({ theme }) => ({
  background: colors.lightPersianGreen,
  color: theme.palette.primary.main,
  width: "256px",
}))

const Save = styled(Btn)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  margin: "16px auto 0 auto",
}))
